<template>
<div class="px-1 pt-1">
  <div v-if="error" class="alert alert-danger mt-2">
    {{ error }}
  </div>
  <VueApexCharts
    v-else-if="stats"
    width="100%"
    :height="height"
    :options="chartOptions"
    :series="series"
  ></VueApexCharts>
</div>
</template>

<script setup lang="ts">
  import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
  import { storeToRefs } from 'pinia';
  import VueApexCharts from 'vue3-apexcharts'
  import { ApexOptions } from 'apexcharts';
  import { useBusinessIntelligenceStore } from "@/stores/businessIntelligence.store.ts";
  import {useI18n} from "vue-i18n";
  const i18n = useI18n();

  const statisticsStore = useBusinessIntelligenceStore();
  const { itemIssues: stats, error } = storeToRefs(statisticsStore);
  const isLoading = ref(false);

  const isDark = ref(document.documentElement.getAttribute('data-bs-theme') === 'dark');
  const themeObserver = new MutationObserver(() => {
    isDark.value = document.documentElement.getAttribute('data-bs-theme') === 'dark';
  });
  themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] });
  onBeforeUnmount(() => themeObserver.disconnect());

  const series = computed(() => {
    return stats.value?.map(x =>
      ({ name: i18n.t(x.label), data: x.values}))
  });
  
  const height = computed(() => {
    if (!stats.value || stats.value.length === 0)
      return '500rem';
    return `${30*(stats.value[0]!.values.length)}rem`
  });

  onMounted(async () => {
    await refresh();
  });

  async function refresh() {
    error.value = "";
    isLoading.value = true;

    try {
      await statisticsStore.fetchItemIssues();
    } finally {
      isLoading.value = false;
    }
  }
  
  const chartOptions = computed<ApexOptions>(() => {
    return {
      chart: {
        id: "Item Issues",
        type: 'bar',
        toolbar: {
          show: false,
        },
        background: 'transparent',
      },
      theme: {
        mode: isDark.value ? 'dark' : 'light',
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '100%',

          dataLabels: {
            position: 'top',
          },
        }
      },
      dataLabels: {
        enabled: true,

        offsetX: 30,
        style: {
          fontSize: '12px',
          colors: [isDark.value ? '#fff' : '#000']
        }
      },
      fill: {
        colors: ['#3C618C', '#979C4B']
      },
      stroke: {
        show: true,
        width: 1,
        colors: [isDark.value ? '#212529' : '#fff']
      },
      tooltip: {
        shared: true,
        intersect: false
      },
      states: {
        normal: {
          filter: { type: 'none', value: 0 },
        },
        hover: {
          filter: { type: 'lighten', value: 0.2 },
        },
        active: {
          filter: { type: 'darken', value: 0.4 },
          allowMultipleDataPointsSelection: false,
        },
      },
      legend: {
        position: 'top',
      },
      xaxis: {
        type: 'category',
        labels: {
          style: {
            fontSize: '12px',
          }
        }
      },
      yaxis: {
        min: 0,
        stepSize: 1,
        show: true
      }
    }
  });
</script>

<style scoped>

</style>